// More on a phone, after SPhoneMore: you (to Settings), Work (Requests, Reminders, the Knowledge base, the Research Center), Find
// (Search, Notifications), and App (dark mode, Settings, sign out).
import { useNavigate } from 'react-router-dom';
import type { SessionUser } from '@dpw/shared';
import { api } from '../api';
import { useSummary } from '../lib/queries';
import { BellButton, P, Row, Section, Title, TopBar } from './kit';
import { useWaitingCount } from './PhoneShell';
import { useFolders } from '../kb/hooks';
import { useVoiceControl } from '../voice/VoiceControl';
import { openPalette } from '../ui/CommandPalette';

const ROLE = { partner: 'Partner', director: 'Director', member: 'Member' } as const;

export function PhoneMore({ me, theme, onTheme }: { me: SessionUser; theme: 'light' | 'dark'; onTheme: () => void }) {
  const sum = useSummary().data;
  const waiting = useWaitingCount();
  const folders = useFolders();
  const nav = useNavigate();
  const vc = useVoiceControl();
  const signOut = async () => { await api.auth.logout().catch(() => undefined); nav('/login'); location.reload(); };
  const cnt = (n: number | undefined, hot?: boolean) => (n ? <span className={`ph-cnt${hot ? ' hot' : ''}`}>{n}</span> : undefined);
  return (
    <div className="ph-page">
      <TopBar><BellButton /></TopBar>
      <Title title="More" />
      <div className="ph-grp">
        <Row to="/settings" className="big" icon={<span className="ph-av lg" aria-hidden="true">{me.initials}</span>} iconTone="bare" title={me.name} sub={`Delta Partners ME · ${ROLE[me.role]}${me.isAdmin ? ', admin' : ''}`} />
      </div>
      <div className="gap16" />
      <div className="ph-grp" role="group" aria-label="Voice">
        <Row onClick={vc.start} icon={<P.mic />} iconTone="ai" title="Voice control" sub="Go to pages, find things, hear the screen, approve by voice" chevron={false} />
      </div>
      <Section title="Work" />
      <div className="ph-grp" role="group" aria-label="Work">
        <Row to="/requests" icon={<P.inbox />} iconTone="amb" title="Requests" sub="Waiting for your approval" right={cnt(waiting, true)} />
        <Row to="/meetings" icon={<P.wave />} title="Meetings" sub="Record, minutes to review, what came out of each" />
        <Row to="/reminders" icon={<P.clock />} title="Reminders" sub="The full list. Due ones also come to Notifications" right={cnt(sum?.overdueReminders, true)} />
        <Row to="/folders" icon={<P.folder />} title="Knowledge base" sub={folders.data ? `${folders.data.length} ${folders.data.length === 1 ? 'folder' : 'folders'} · documents with summaries` : 'Documents with summaries'} />
        <Row to="/knowledge" icon={<P.book />} iconTone="grn" title="Research Center" sub="Insights, following, research" />
      </div>
      <Section title="Find" />
      <div className="ph-grp" role="group" aria-label="Find">
        <Row onClick={openPalette} icon={<P.search />} title="Search" chevron={false} />
        <Row to="/notifications" icon={<P.bell />} title="Notifications" right={cnt(sum?.notifications, true)} />
      </div>
      <Section title="App" />
      <div className="ph-grp" role="group" aria-label="App">
        <button type="button" className="ph-row" role="switch" aria-checked={theme === 'dark'} onClick={onTheme}>
          <span className="ph-ic"><P.moon /></span><span className="ph-rt"><div className="t">Dark mode</div></span><span className={`ph-sw${theme === 'dark' ? ' on' : ''}`} aria-hidden="true" />
        </button>
        <Row to="/settings" icon={<P.gear />} title="Settings" sub="Team, spaces, notifications, usage" />
        {me.isAdmin && <Row to="/settings/usage" icon={<P.list />} title="Usage" sub="Admins only: time in the app, service costs" />}
        <Row onClick={() => void signOut()} icon={<P.x />} title="Sign out" sub={me.email} chevron={false} />
      </div>
    </div>
  );
}
